import { Component, OnInit, Input, ElementRef, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { GameService } from '../../services/game.service';
import { AudioText, GameMemory } from '../../_models/model';
import { ConfigService } from '../../services/config.service';
import { AudioPlayerService } from '../../services/audio-player.service';
import { IGameComponent } from '../game/game.component';

interface ISet {
      a: string,
    b: string,
    a_text: AudioText,
    solved: boolean,
    setId: number
}

type CardGroupType = 'a' | 'b';

@Component({
  selector: 'app-game-memory',
  templateUrl: './game-memory.component.html',
  styleUrls: ['./game-memory.component.less']
})

export class GameMemoryComponent implements OnInit , IGameComponent<GameMemory> {
  @Output() closeEvent = new EventEmitter<boolean>();
  public activeGame?: GameMemory;
  public explanationText?: string;

  cardGroup: any;
  @Input() public game!: string;
  @Input() public gameType!: string;
  @Input() public activeLevel!: string;
  private card1: number | null = null;
  private card2: number | null = null;
  private setId: number | null = null;
  private cardElements: {[key: string]: HTMLElement} = {};
  private busy = false;
  private sets: ISet[] =[];

  constructor(
    private elRef: ElementRef,
    private gameService: GameService,
    private configService: ConfigService,
    public audio: AudioPlayerService) {
  }

  ngOnInit() {
    this.explanationText = this.audio.getPlay('memory_uitleg');
  }

  public setGame(game: GameMemory) {
    this.activeGame = game;

    // Load sets from config sevice
    game.sets.forEach((set, index) => {
      this.sets.push({
        a: this.configService.getStaticUri() + set.imageA,
        b: this.configService.getStaticUri() + set.imageB,
        a_text: set.imageA_text,
        solved: false,
        setId: index
      });
    });

    for (const card of this.elRef.nativeElement.querySelectorAll('.card')) {
      card.addEventListener('click', this.cardClick.bind(this));
      const XCardId = +card.getAttribute('x-card');
      this.cardElements[XCardId] = card;
      card.classList.add('prepare3d');
      card.classList.remove('prepare3d');
    }

    // register positions to shuffle cards
    const positions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    for (const set of this.sets) {
      const rand1 = Math.floor(Math.random() * positions.length);
      // rand1 = 0;
      const a = positions.splice(rand1, 1)[0];

      const rand2 = Math.floor(Math.random() * positions.length);
      // rand2 = 0;
      const b = positions.splice(rand2, 1)[0];

      // console.log(a, b);
      const imgA = this.cardElements[a].querySelector('.back') as HTMLImageElement;
      const imgB = this.cardElements[b].querySelector('.back') as HTMLImageElement;
      this.cardElements[a].setAttribute('x-set', set.setId.toString());
      this.cardElements[a].setAttribute('x-group', 'a');
      this.cardElements[b].setAttribute('x-set', set.setId.toString());
      this.cardElements[b].setAttribute('x-group', 'b');

      imgA.src = set.a;
      imgB.src = set.b;
    }
  }

  /**
   * Register a card click
   * @param event
   */
  cardClick(event: Event) {
    if (this.busy) {
      // Er is nog een animatie oid bezig, niets doen
      return;
    }
    // Set game to busy while handling this click, no other card clicks are handled during this period.
    this.busy = true;

    // Stel in waarop is geklikt
    const card: HTMLElement = (event.target as any).closest('.card');      // object van van de geklikte kaart
    const cardId: number = +(card.getAttribute('x-card') as string);     // id van geklikte kaart
    const cardGroup: CardGroupType = card.getAttribute('x-group') as CardGroupType;  // a or b
    const setId: number = +(card.getAttribute('x-set') as string);       // SET id

    // Already solved card, do nothing
    if (this.sets.find(x => x.setId === setId)?.solved) {
      this.busy = false;
      return;
    }

    this.enableCards();

    // Same card. Unselect and back to default position
    if (this.card1 === cardId || this.card2 === cardId) {
      if (this.card1) {
        this.cardElements[this.card1.toString()].classList.remove('flipped');
        this.cardElements[this.card1.toString()].classList.remove('shaking');
      }
      this.card1 = null;
      this.setId = null;
      this.busy = false;
      return;
    }

    // Play the AudioText file
    if (cardGroup === 'a') {
      const set = this.sets.find(x => x.setId === setId);
      if (!set) {
        throw Error('Set not found');
      }
      this.audio.playAudio(set.a_text);
    }

    // loop over alle andere kaarten van dezelfde groep (A of B) en zet die op disabled
    this.disableGroup(cardGroup, cardId);

    // this.card1 bevat eerste selecie, dus dit is de tweede click en van dezelfde groep (A of B)
    // Remove active state van reeds geselecteerde kaart (deselect / terug naar uitgangspositie)
     if (this.card1 && this.cardGroup === cardGroup) {
       this.cardElements[this.card1.toString()].classList.remove('flipped');
       this.card1 = null;
     }

    // Highlight this card
    this.cardElements[cardId.toString()].classList.add('flipped');

    // Store first card  (this is the first selected card)
    // if A, grey all other A cards, if B, grey all other B cards
    if (!this.card1) {
      this.card1 = cardId;
      this.busy = false;
      this.setId = setId;
      this.cardGroup = cardGroup;
      return;
    }

    // Continue with card 2
    this.card2 = cardId;

    // The Set is completed / same x-set ID
    if (this.setId === setId) {
      this.goodMatch();
    } else {
      this.wrongMatch();
    }
  }

  private goodMatch() {
    // set card layouts to solved
      // Highlight second card
      const card1 = this.card1;
      const card2 = this.card2;
      const set = this.sets.find(x => x.setId === this.setId);
      if (!card2) {
        throw Error('Card 2 not set but expected');
      }
      if (!card1 || !card2) {
        throw Error('Card 1 not set but expected');
      }
      if (!set) {
        throw Error('Set expected');
      }

      this.cardElements[card2.toString()].classList.add('flipped');

      // Audio and Visual feedback
      this.audio.playSuccess();
      const card1Node = this.cardElements[card1.toString()].parentNode as HTMLElement;
      const card2Node = this.cardElements[card2.toString()].parentNode as HTMLElement;
      card1Node.classList.add('solved');
      card2Node.classList.add('solved');

      // After short timeout, show solved and reset game
      setTimeout(() => {
        this.cardElements[card1.toString()].classList.remove('flipped');
        this.cardElements[card2.toString()].classList.remove('flipped');
        this.card1 = null;
        this.card2 = null;
        this.setId = null;
        this.busy = false;
      }, 400);


      // set the set to solved and reset active cards
      // Register set as solved
      set.solved = true;

      // check for unsolved sets
      if (this.sets.find(x => x.solved === false)) {
        // There are still unsolved sets
      } else {
        this.gameEnded();
      }
      this.enableCards();
  }

  private enableCards() {
    // Enable all cards
    for (const otherCard of this.elRef.nativeElement.querySelectorAll('.card')) {
      otherCard.parentNode.classList.remove('disabled');
    }
  }

  private disableGroup(groupType: CardGroupType, selectedCardId: number) {
    for (const otherCard of this.elRef.nativeElement.querySelectorAll('[x-group=' + groupType + ']')) {
      if (+otherCard.getAttribute('x-card') !== selectedCardId) {
        const set = this.sets.find(x => x.setId === +otherCard.getAttribute('x-set'));
        if (set?.solved) {
          otherCard.parentNode.classList.add('disabled');
        }
      }
    }
  }

  private gameEnded() {
    // Close game after a timeout so user can see the last matches and time to see animated vinkje
    setTimeout(() => {
      if (!this.activeGame) {
        throw Error('no active game');
      }
      // store game result and exit game
      this.gameService.setLevelProgress(this.activeGame.levelElement.uuid, '', true);
      this.closeEvent.emit();
    }, 1300);
  }

  private wrongMatch() {
    // No match, set all unsolved cards to initial state
    this.audio.playError();
    this.enableCards();
    const card1 = this.card1;
    const card2 = this.card2;
    if (!card1 || !card2) {
        throw Error('Card 1 not set but expected');
    }


    this.cardElements[card1.toString()].classList.add('shaking');
    this.cardElements[card2.toString()].classList.add('shaking');
    setTimeout(() => {
      this.cardElements[card1.toString()].classList.remove('flipped');
      this.cardElements[card2.toString()].classList.remove('flipped');
      this.cardElements[card1.toString()].classList.remove('shaking');
      this.cardElements[card2.toString()].classList.remove('shaking');
      this.card1 = null;
      this.card2 = null;
      this.setId = null;
      this.busy = false;
    }, 1700);
  }
  done() {
    this.gameEnded();
  }
}
